
import {
  circleData,
  Description,
  GPData,
  LegendGlobal,
  Parameter,
  pinData,
  SearchItems,
  sigleAntwort
} from '../static/apiModels';
import {
  arrangeBySigle,
  hslToHex,
  loadData,
  selectColor
} from '@/helpers/helper';
import { Component, Vue, Prop } from 'vue-property-decorator';
import DataSwitch from './DataSwitch.vue';
import AudioPlayer from './AudioPlayer.vue';
import ActionButtons from './ActionButtons.vue';
import { isAufgabeStandard } from '@/helpers/helper';
import { aufgabenModule } from '@/store/modules/aufgaben';
import { legendMod } from '@/store/modules/legend';
import { convertHslToStr } from '@/helpers/helper';

import {
  Antwort,
  antwortenDto,
  AntwortToken,
  AntwortTokenStamp,
  AufgabeStamp,
  selectionObject
} from '@/api/dioe-public-api';
import { isArray } from 'lodash';

@Component({
  components: { DataSwitch, AudioPlayer, ActionButtons },
  name: 'AudioCard'
})
export default class DragableCard extends Vue {
  @Prop() readonly data!: pinData;

  @Prop(Boolean) readonly showDataSideways!: boolean;

  aufgIdx: number = 0;
  antIdx: number = 0;

  AM = aufgabenModule;
  LM = legendMod;

  get antVariation() {
    return this.AM.antVariation;
  }

  get aufgabenLoading() {
    return (
      this.AM.loading &&
      this.data.antwortAudio.length === 0 &&
      this.data.aufgabeAudio.length === 0
    );
  }

  get ageRange() {
    return legendMod.ageRange;
  }

  audioData(d: sigleAntwort) {
    return d.data[this.antIdx].data[0];
  }

  arrangeBySigle(data: AntwortTokenStamp[] | AufgabeStamp[]): sigleAntwort[] {
    return arrangeBySigle(data);
  }

  getType(val: string | null): string {
    if (!val) return '';
    return isAufgabeStandard(val);
  }

  pinCard(event: any, pinData: boolean) {
    this.LM.editPinnedShowById({
      dataId: this.data.id,
      show: true,
      pinned: pinData
    });
  }

  removeElement() {
    this.LM.removeElementFromPinDataById(this.data.id);
  }

  switchData(dir: boolean) {
    if (this.data.selectedOrt) {
      // increment the index
      let idx;
      if (dir) {
        idx =
          this.data.selectedDataIdx === this.data.selectedOrt.data.length - 1
            ? 0
            : this.data.selectedDataIdx + 1;
        // decrease the index
      } else {
        idx =
          this.data.selectedDataIdx === 0
            ? this.data.selectedOrt.data.length - 1
            : this.data.selectedDataIdx - 1;
      }
      let data = this.LM.pinnedData.filter((el) => el.id === this.data.id)[0];
      data.selectedDataIdx = idx;
      this.LM.editPinnedByID(data);
      const d = data.selectedOrt.data[this.data.selectedDataIdx];
      loadData(d, data.selectedOrt.osm, d.t, this.ageRange).then(() => {
        switch (d.t) {
          case SearchItems.Phaen:
          case SearchItems.Query:
          case SearchItems.Tag:
            data.antwortAudio = this.AM.antwortenAudio;
            break;
          case SearchItems.Aufgaben:
            data.aufgabeAudio = this.AM.aufgabeSingleOrt;
            break;
        }
        this.LM.editPinnedByID(data);
      });
    }
  }

  async evaluateDataForLocation(ort: string | undefined) {
    const title = `Auswertung für ${ort ? ort : ''}`;
    this.AM.setDiagramTitle(`Auswertung für ${ort ? ort : ''}`);
    let curr = await this.LM.pushNewPinDataVar({
      diagramTitle: title,
      isPinned: false,
      isShown: true,
      diagramData: []
    });
    let res: Array<Description> = [];
    let gpData: Array<GPData> = [];
    const currEle = this.data.selectedOrt?.data[this.data.selectedDataIdx];
    if (this.data.antwortAudio.length > 0) {
      this.data.antwortAudio.forEach((antAudio) => {
        const val = antAudio.res.reduce((prev, curr) => {
          return prev + curr.data.length;
        }, 0);
        const idx = res.findIndex((e) => e.sigle === antAudio.sigle);
        antAudio.res.forEach((antRes) => {
          antRes.data.forEach((data) => {
            const idxGp = gpData.findIndex(
              (e) => e.sigle === antAudio.sigle && e.erhArtId === data.erhArtId
            );
            if (idxGp < 0) {
              gpData.push({
                sigle: antAudio.sigle,
                count: 1,
                erhArt: data.erhArt,
                erhArtId: data.erhArtId,
                bezErhebung: data.bezErhebung
              });
            } else {
              gpData[idxGp].count++;
            }
          });
        });
        // Could not find entry - Create new one
        if (idx < 0) {
          res.push({
            age: antAudio.age,
            sigle: antAudio.sigle,
            group: antAudio?.gruppeBez ? antAudio.gruppeBez : '',
            value: val,
            color: currEle.c,
            name: currEle.name,
            gp: gpData.filter((gpdata) => gpdata.sigle === antAudio.sigle)
          });
        } else {
          // Exists - Add value to found entry
          res[idx].value += val;
          res[idx].gp = gpData.filter(
            (gpdata) => gpdata.sigle === antAudio.sigle
          );
        }
      });
    }
    if (this.data.aufgabeAudio.length > 0) {
      this.data.aufgabeAudio.forEach((el) => {
        const val = el.data.length;
        const gpIdx = res.findIndex((e) => e.sigle === el.sigle);
        if (gpIdx < 0) {
          res.push({
            age: el.age,
            sigle: el.sigle,
            group: el?.gruppeBez ? el.gruppeBez : '',
            value: val,
            color: currEle.c,
            name: currEle.name,
            gp: []
          });
        } else {
          res[gpIdx].value += val;
        }
      });
    }
    if (this.data.selectedOrt) {
      let data = this.data.selectedOrt.data.filter(
        (el) => el.id !== currEle.id
      );
      let dto: Array<antwortenDto> = [];
      for (const key of data) {
        let ids = [] as number[];
        if (key.t !== SearchItems.Query) {
          continue;
        }
        if (key.id !== '') {
          ids = [Number(key.id)];
        }
        let token = [] as selectionObject[];
        let lemma = [] as selectionObject[];
        let max = this.ageRange.upper;
        let min = this.ageRange.lower;
        const p = key.para;
        let group = false;
        if (p) {
          token = p.textTokenList ? p.textTokenList : [];
          lemma = p.lemmaList ? p.lemmaList : [];
          ids = p.tagList && p.tagList.length > 0 ? p.tagList[0].tagIds : [-1];
          group = ids.length > 0;
        }
        dto.push({
          ids: ids,
          paraid: key.id,
          osmId: this.data.selectedOrt.osm,
          ageLower: min,
          ageUpper: max,
          text: token,
          ausbildung: p?.maxEducation ? p.maxEducation : undefined,
          beruf_id: p?.education != undefined ? p.education : undefined,
          weiblich: p?.gender !== undefined ? p.gender : undefined,
          project: p?.project ? p.project : undefined,
          erhArt: legendMod.erhArtFilter,
          lemma: lemma,
          group: group
        });
      }
      await this.AM.fetchAntwortVariation(dto);
      const variation = this.antVariation;
      gpData = [];

      variation.forEach((varElement) => {
        const val = varElement.res.reduce((prev, curr) => {
          return prev + curr.data.length;
        }, 0);
        varElement.res.forEach((antRes) => {
          const id = antRes.id;
          const legend = this.data.selectedOrt.data.find(
            (dataElement) => dataElement.id === id
          );
          const idx = res.findIndex(
            (e) => e.sigle === varElement.sigle && e.name === legend?.name
          );
          antRes.data.forEach((data) => {
            const idxGp = gpData.findIndex(
              (e) =>
                e.sigle === varElement.sigle && e.erhArtId === data.erhArtId
            );
            if (idxGp < 0) {
              gpData.push({
                sigle: varElement.sigle,
                count: 1,
                erhArt: data.erhArt,
                erhArtId: data.erhArtId,
                bezErhebung: data.bezErhebung
              });
            } else {
              gpData[idxGp].count++;
            }
          });
          if (idx < 0) {
            res.push({
              age: varElement.age,
              sigle: varElement.sigle,
              group: varElement?.gruppeBez ? varElement.gruppeBez : '',
              value: val,
              color: legend ? legend.c : '#FFF',
              name: legend ? legend.name : '',
              gp: gpData.filter((gpdata) => gpdata.sigle === varElement.sigle)
            });
          } else {
            // Exists - Add value to found entry
            res[idx].value += val;
            res[idx].gp = gpData.filter(
              (gpdata) => gpdata.sigle === varElement.sigle
            );
          }
        });
      });
    }
    res.forEach((el) => {
      const color = selectColor(null);
      el.color = hslToHex(color.h, color.s * 100, color.l * 100);
    });
    curr.diagramData = res;
    this.LM.editPinnDataVar(curr);
    this.AM.setDiagramData(res);
  }

  async evaluateData(ort: string | undefined, sigle: string, d: sigleAntwort) {
    const title = `Auswertung für sigle ${sigle} in ${ort ? ort : ''}`;
    this.AM.setDiagramTitle(
      `Auswertung für sigle ${sigle} in ${ort ? ort : ''}`
    );
    let curr = await this.LM.pushNewPinDataVar({
      diagramTitle: title,
      isPinned: false,
      isShown: true,
      diagramData: []
    });
    let res: Array<Description> = [];
    if (this.data.selectedOrt) {
      const currEle = this.data.selectedOrt?.data[this.data.selectedDataIdx];
      const val = d.data.reduce((acc, curr) => {
        const data = curr.data[0];
        if (isArray(data)) {
          return acc + data.length;
        }
        return acc + curr.data.length;
      }, 0);
      let data = this.data.selectedOrt.data.filter(
        (el) => el.id !== currEle.id
      );
      let dto: Array<antwortenDto> = [];
      for (const key of data) {
        let ids = [] as number[];
        if (key.t !== SearchItems.Query) {
          continue;
        }
        if (key.id !== '') {
          ids = [Number(key.id)];
        }
        let token = [] as selectionObject[];
        let lemma = [] as selectionObject[];
        let max = this.ageRange.upper;
        let min = this.ageRange.lower;
        const p = key.para;
        let group = false;
        if (p) {
          max = Math.max(p.ageRange[1], max);
          min = Math.min(p.ageRange[0], min > -1 ? min : p.ageRange[0]);
          token = p.textTokenList ? p.textTokenList : [];
          lemma = p.lemmaList ? p.lemmaList : [];
          ids = p.tagList && p.tagList.length > 0 ? p.tagList[0].tagIds : [-1];
          group = ids.length > 0;
        }
        dto.push({
          ids: ids,
          paraid: key.id,
          osmId: this.data.selectedOrt.osm,
          ageLower: d.age,
          ageUpper: d.age,
          text: token,
          ausbildung: p?.maxEducation ? p.maxEducation : undefined,
          beruf_id: p?.education != undefined ? p.education : undefined,
          weiblich: p?.gender !== undefined ? p.gender : undefined,
          project: p?.project ? p.project : undefined,
          erhArt: legendMod.erhArtFilter,
          lemma: lemma,
          group: group
        });
      }
      await this.AM.fetchAntwortVariation(dto);
      const variation = this.antVariation;
      const person = variation.find((el: any) => el.sigle === sigle);
      res.push({
        color: currEle.c,
        name: currEle.name,
        value: val,
        age: person?.age ? person.age : 0,
        sigle: person?.sigle ? person.sigle : '',
        group: person?.gruppeBez ? person.gruppeBez : '',
        erhArt: '',
        erhArtId: -1,
        bezErhebung: ''
      });
      person?.res.forEach((el: any) => {
        const d = data.find((e) => e.id === el.id);
        res.push({
          color: d ? d.c : '#000',
          name: d ? d.name : '',
          value: el.data.length,
          age: person?.age ? person.age : 0,
          sigle: person?.sigle ? person.sigle : '',
          group: person?.gruppeBez ? person.gruppeBez : '',
          erhArt: '',
          erhArtId: -1,
          bezErhebung: ''
        });
      });
    }
    curr.diagramData = res;
    this.LM.editPinnDataVar(curr);
    this.AM.setDiagramData(res);
  }
}
